// @flow
import type { Node } from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import {
  Provider as StoreProvider,
  useDispatch,
  useSelector
} from 'react-redux';

import { ImageProvider } from '@contexts/image';
import { VideoProvider, VideoContext } from '@contexts/video';
import { SkuContext, SkuProvider } from '@contexts/sku';
import {
  BandStateContext,
  BandDispatchContext,
  BandProvider
} from '@contexts/band';

import useProductDetailColumnCount from '@hooks/useProductDetailColumnCount';

import DyorDataWrapper from '@components/DyorDataWrapper';

import DesignYourOwnRing from '@components/dyor/Container';
import PageFooter from '@components/productDetail/PageFooter.jsx';
import MatchingSettings from '@components/productDetail/bands/MatchingSettings';
import OtherMetals from '@components/productDetail/bands/OtherMetals';
import Specifications from '@components/productDetail/bands/Specifications';
import ShippingAndActions from '@components/productDetail/bands/ShippingAndActions';
import ImageThumbnails from '@components/productDetail/bands/ImageThumbnails';
import DescriptionAndPrice from '@components/productDetail/bands/DescriptionAndPrice';
import MainImage from '@components/productDetail/bands/MainImage';

import GetUpdatedProductData from '../../async_data_retrievers/Band';

import ErrorBoundary from '@components/ErrorBoundary';

import AxiosInstanceGenerator from '@src/utils/axiosInstance';
import { handleError } from '../../../utils/errors';

import * as routes from '@src/app_routes/bands';

import css from '@styles/productDetail';
import { itemIndexableSeries } from '../../../utils/favorites';

import ViewItemEvent from '@components/productDetail/googleAnalyticsEvents/viewItem/band';

import type { TBandDetail } from '@types/band';
import type { RailsContextType } from '@types/misc';

declare var Honeybadger: any;

type TContainerProps = {
  children: Node
};

type PropsType = {};

const urlMatcher = /\/(?:wedding-)?band(?:s)?\/detail(?:\/[^/].+)?\/(.*)$/;
const loadingIcon = '/images/loading-diamond.gif';

function ImageSection() {
  const { toggleVideoState } = useContext(VideoContext);
  const { band }: { band: TBandDetail } = useContext(BandStateContext);
  const dispatch = useDispatch();
  const isFavorited = useSelector(state => {
    const series = state.settingFavorites.series || [];

    return (
      series.indexOf(
        itemIndexableSeries(band.lumera_series, band.raw_metal)
      ) !== -1
    );
  });

  const handleFavoriteToggle = useCallback(() => {
    dispatch({
      type: 'TOGGLE_FAVORITE_SETTING',
      item: {
        sku: band.vendor_sku,
        series: itemIndexableSeries(band.lumera_series, band.raw_metal)
      }
    });
  }, [dispatch, band.vendor_sku, band.lumera_series, band.raw_metal]);

  return (
    <MainImage
      onClick={toggleVideoState}
      isFavorited={isFavorited}
      onToggleFavorite={handleFavoriteToggle}
    />
  );
}

function Container({ children }: TContainerProps) {
  return (
    <div className={`${css.container} product-detail--container band`}>
      {' '}
      {children}{' '}
    </div>
  );
}

function LeftAndMiddle() {
  return (
    <div className={css.leftAndMiddle}>
      <Left />
      <Middle />
    </div>
  );
}

function Left() {
  return (
    <div className={css.left}>
      <Specifications />
    </div>
  );
}

function Middle() {
  return (
    <div className={css.middle}>
      <ImageThumbnails />
      <OtherMetals />
      <MatchingSettings />
      {/*
        We use flexbox order: -1 to display this first. It is placed here in
       the markup because otherwise other elements display in front of it
       thanks to absolute positioning.
      */}
      <ImageSection />
    </div>
  );
}

function Right() {
  const { band }: { band: TBandDetail } = useContext(BandStateContext);

  return (
    <div className={css.right}>
      <DescriptionAndPrice band={band} />
      <ShippingAndActions band={band} />
    </div>
  );
}

function Loading() {
  return (
    <Container>
      <div className={css.loading}>
        <div>
          <img src={loadingIcon} alt='Loading icon' />
        </div>
        <p>Loading band...</p>
      </div>
    </Container>
  );
}

const GetBand = React.memo(function GetBand() {
  const [failed, setFailed] = useState(false);
  const { setBand } = useContext(BandDispatchContext);
  const dispatch = useDispatch();
  const { sku, setSku } = useContext(SkuContext);

  useEffect(() => {
    const getBand = async () => {
      if (!sku) {
        const locationMatches =
          window && window.location.pathname.match(urlMatcher);
        const sku = locationMatches && locationMatches[1];

        if (!sku) {
          throw new Error('Unable to parse SKU from path.');
        }

        setSku(sku);
        return;
      }

      const axios = AxiosInstanceGenerator();

      // When the SKU is changed on the ring size selector, we want to show the
      // loading page
      setBand(null);

      try {
        const res = await axios.get(routes.apiDetail(sku), {
          headers: { 'Cache-Control': 'maxage=1200' }
        });

        setBand(res.data);

        if (typeof document !== 'undefined') {
          document.title = res.data.meta_title;
          const desc = document.querySelector('meta[name=description]');

          desc && desc.setAttribute('content', res.data.meta_description);
        }

        dispatch({
          type: 'PUSH_RECENTLY_VIEWED_BAND',
          payload: res.data
        });
      } catch (e) {
        if (typeof Honeybadger !== 'undefined') {
          Honeybadger.notify(e);
        }

        setFailed(true);
      }
    };

    getBand().catch(handleError);
  }, [sku, dispatch, setBand, setSku]);

  if (failed) {
    throw new Error('Loading from the API failed');
  }

  return null;
});

function MultipleColumns() {
  return (
    <Container>
      <LeftAndMiddle />
      <Right />
    </Container>
  );
}

function SingleColumn() {
  const { band }: { band: TBandDetail } = useContext(BandStateContext);

  return (
    <Container>
      <div className={css.singleColumn}>
        <DescriptionAndPrice band={band} />
        <ShippingAndActions band={band} />
        <ImageSection />
        <ImageThumbnails />
        <Specifications />
        <OtherMetals />
        <MatchingSettings />
      </div>
    </Container>
  );
}

function ProductDetail({ railsContext }) {
  const dyorSettingSku = useSelector(state => state.ringBuilder.setting.sku);
  const dyorDiamondId = useSelector(state => state.ringBuilder.diamond.id);

  const { band }: { band: TBandDetail } = useContext(BandStateContext);
  const urlSkuRef = useRef(null);

  const columns = useProductDetailColumnCount();

  // We only want to set it once, but not in useEffect because we need to do it
  // before DiamondCompatibility is mounted.
  if (!railsContext.serverSide && !urlSkuRef.current) {
    const locationMatches =
      window && window.location.pathname.match(urlMatcher);
    const sku = locationMatches && locationMatches[1];

    if (!sku) {
      throw new Error('Unable to parse SKU from path.');
    }

    urlSkuRef.current = sku;
  }

  if (band) {
    return (
      <div>
        <ViewItemEvent band={band} />
        <GetUpdatedProductData />
        {(dyorSettingSku || dyorDiamondId) && <DesignYourOwnRing />}
        {columns > 1 ? <MultipleColumns /> : <SingleColumn />}
        <PageFooter />
      </div>
    );
  } else {
    return <Loading />;
  }
}

function ErrorLoading() {
  return (
    <Container>
      <div>
        <h1>Something went wrong</h1>
        <p>
          Unfortunately, we were not able to load the item you are looking for.
          Please refresh the browser to try again.
        </p>
      </div>
    </Container>
  );
}

// We have to accept `railsContext` for client-side renders, while it comes from
// `useSSRComponent` in `props` on server-side renders.
export default (props: PropsType, railsContext: RailsContextType = {}) => (
  <ErrorBoundary fallbackComponent={<ErrorLoading />}>
    <StoreProvider store={ReactOnRails.getStore('SharedReduxStore')}>
      {/* Don't pull in band until client render has hydrated */}
      <DyorDataWrapper fallback={<Loading />}>
        <SkuProvider>
          <ImageProvider>
            <VideoProvider>
              <BandProvider>
                <GetBand />
                <ProductDetail {...{ railsContext, ...props }} />
              </BandProvider>
            </VideoProvider>
          </ImageProvider>
        </SkuProvider>
      </DyorDataWrapper>
    </StoreProvider>
  </ErrorBoundary>
);
